import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, UserRound } from 'lucide-react';
import FeaturedLayout from '../layouts/FeaturedLayout';
import { Button, Card, CardContent, Dialog, DialogContent, DialogTitle } from '../ui';
import { getCredencialesRival } from '../../lib/services/credencialesRival';
import { mensajeCerradas, textoVigencia, RECARGA_MS } from '../../lib/credencialesRival';
import { etiquetaDivision } from '../../lib/division';
import { cn, fixMojibake } from '../../lib/utils';

// Las credenciales del rival de un partido de la red (spec 2026-10-15 §5): foto, numero y nombre
// de sus jugadores y de su cuerpo tecnico, para verificar quien juega. La base decide si se abren
// (credenciales_rival). Las fotos son URLs que caducan a los 10 minutos: la pagina se recarga sola
// a los 9. No hay descarga ni boton de compartir.

const Foto = ({ url, nombre, onError }) => {
  if (!url) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground">
        <UserRound className="h-6 w-6" />
        <span className="text-[11px]">Sin foto</span>
      </div>
    );
  }
  return (
    <img src={url} alt={nombre} draggable={false} onError={onError} className="h-full w-full object-cover" />
  );
};

const NumeroYNombre = ({ numero, nombre, testId }) => (
  <>
    {numero != null && <><span data-testid={testId} className="mr-0.5 font-bold tabular-nums">{numero}</span>{' '}</>}
    {nombre}
  </>
);

const CLASES_TARJETA = 'overflow-hidden rounded-lg border bg-card text-left text-card-foreground shadow-sm';

// Credencial chica: foto cuadrada y abajo numero y nombre en la misma fila. Con foto, al tocarla se
// abre en grande para comparar la cara. Si la foto no carga (o la URL ya vencio), queda "Sin foto" y
// deja de abrirse; con la URL nueva de la siguiente recarga se vuelve a intentar.
const Tarjeta = ({ persona }) => {
  const nombre = fixMojibake(persona.nombre || '');
  const [urlRota, setUrlRota] = useState(null);
  const [abierta, setAbierta] = useState(false);
  const url = persona.foto_url && persona.foto_url !== urlRota ? persona.foto_url : null;
  const marcarRota = () => setUrlRota(persona.foto_url);

  const contenido = (
    <>
      <div className="aspect-square bg-muted">
        <Foto url={url} nombre={nombre} onError={marcarRota} />
      </div>
      <p className="line-clamp-2 px-2 py-1.5 text-xs font-medium leading-tight sm:text-sm">
        <NumeroYNombre numero={persona.numero} nombre={nombre} testId="numero" />
      </p>
    </>
  );

  if (!url) return <div className={CLASES_TARJETA}>{contenido}</div>;
  return (
    <>
      <button
        type="button"
        aria-label={`Ver foto de ${nombre}`}
        onClick={() => setAbierta(true)}
        className={cn(CLASES_TARJETA, 'block w-full transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring')}
      >
        {contenido}
      </button>
      <Dialog open={abierta} onOpenChange={setAbierta}>
        <DialogContent compact aria-describedby={undefined} className="max-w-sm gap-3 p-4">
          <DialogTitle className="pr-6 text-base">
            <NumeroYNombre numero={persona.numero} nombre={nombre} />
          </DialogTitle>
          <img
            src={url}
            alt={nombre}
            draggable={false}
            onError={marcarRota}
            className="aspect-square w-full rounded-md bg-muted object-cover"
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

// Tres por fila en el telefono, cuatro en tablet y seis en escritorio.
const REJILLA = 'grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6';

const subtituloDe = (datos) => {
  if (!datos) return '';
  const { rival } = datos;
  if (!rival) return 'Rival por definir';
  const division = rival.categoria
    ? etiquetaDivision({ nombre: fixMojibake(rival.categoria), rama: rival.rama })
    : null;
  const equipo = rival.equipo ? fixMojibake(rival.equipo) : null;
  const club = rival.club ? fixMojibake(rival.club) : null;
  // El club no se repite si el nombre del equipo ya lo trae ("Pumas Demo Sub-10" y "Pumas Demo").
  const clubAparte = club && !(equipo || '').toLowerCase().includes(club.toLowerCase()) ? club : null;
  return [equipo, clubAparte, division].filter(Boolean).join(' · ');
};

const CredencialesRival = () => {
  const { juegoId } = useParams();
  const navigate = useNavigate();
  const [estado, setEstado] = useState({ cargando: true, datos: null, error: null });

  const cargar = useCallback(async () => {
    const { data, error, code } = await getCredencialesRival(juegoId);
    setEstado({ cargando: false, datos: error ? null : data, error: error ? (code || 'error') : null });
  }, [juegoId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Las fotos caducan a los 10 minutos: se vuelve a pedir todo a los 9 de cada carga, mientras la
  // pagina este abierta (abiertas o cerradas; no tras un error). Si para entonces la ventana ya
  // cerro, la pagina cambia al mensaje y deja de mostrar fotos; si ya abrio, las muestra.
  useEffect(() => {
    if (!estado.datos) return undefined;
    const t = setTimeout(cargar, RECARGA_MS);
    return () => clearTimeout(t);
  }, [estado.datos, cargar]);

  // Con el telefono bloqueado los temporizadores se detienen: al volver, se piden de nuevo (la ventana
  // pudo cerrar y las fotos vencer).
  useEffect(() => {
    const alVolver = () => { if (document.visibilityState === 'visible') cargar(); };
    document.addEventListener('visibilitychange', alVolver);
    return () => document.removeEventListener('visibilitychange', alVolver);
  }, [cargar]);

  const reintentar = () => {
    setEstado({ cargando: true, datos: null, error: null });
    cargar();
  };
  const volver = () => navigate(`/torneos/juegos/${juegoId}`);
  const { cargando, datos, error } = estado;

  const aviso = (texto, accion) => (
    <Card>
      <CardContent className="space-y-4 py-10 text-center">
        <p className="text-sm text-muted-foreground">{texto}</p>
        {accion}
      </CardContent>
    </Card>
  );
  const botonVolver = (
    <Button variant="outline" onClick={volver}>
      <ArrowLeft className="mr-1 h-4 w-4" />
      Volver al partido
    </Button>
  );

  let cuerpo;
  if (cargando) {
    cuerpo = <p className="py-10 text-center text-sm text-muted-foreground">Cargando credenciales...</p>;
  } else if (error === 'sin_permiso') {
    cuerpo = aviso('No tienes acceso a las credenciales de este partido.', botonVolver);
  } else if (error) {
    cuerpo = aviso('No se pudieron cargar las credenciales.', <Button onClick={reintentar}>Reintentar</Button>);
  } else if (datos?.estado !== 'abierta') {
    cuerpo = aviso(mensajeCerradas(datos?.motivo, { abre: datos?.abre }), botonVolver);
  } else {
    const jugadores = datos.jugadores || [];
    const staff = datos.staff || [];
    cuerpo = (
      <>
        <div className="flex items-center gap-3">
          {datos.rival?.logo_url && (
            <img src={datos.rival.logo_url} alt="" className="h-10 w-10 shrink-0 rounded object-contain" />
          )}
          <p className="text-sm text-muted-foreground">{textoVigencia(datos.cierra)}</p>
        </div>
        {jugadores.length === 0 ? (
          <p className="text-sm text-muted-foreground">El equipo rival no tiene jugadores convocados.</p>
        ) : (
          <div className={REJILLA}>
            {jugadores.map((p) => <Tarjeta key={p.id} persona={p} />)}
          </div>
        )}
        {staff.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-base font-semibold">Cuerpo técnico</h2>
            <div className={REJILLA}>
              {staff.map((s) => <Tarjeta key={s.id} persona={s} />)}
            </div>
          </section>
        )}
        <div>{botonVolver}</div>
      </>
    );
  }

  const rivalTexto = subtituloDe(datos);
  return (
    <FeaturedLayout title="Credenciales del rival" subtitle={rivalTexto} refreshAction={cargar}>
      <div className="space-y-4">
        {/* El subtitulo del layout no sale en el telefono (hidden md:block): ahi se repite aqui. */}
        {rivalTexto && <p data-testid="rival" className="text-sm font-medium md:hidden">{rivalTexto}</p>}
        {cuerpo}
      </div>
    </FeaturedLayout>
  );
};

export default CredencialesRival;
